import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../contexts/Auth';
import {
  getAppearancePreferences,
  putAppearancePreferences,
} from '../api/adaFetch';
import { brandColorById, brandIconSVG, DEFAULT_BRAND_COLOR } from './brand';

const AppearanceContext = createContext(null);
const emptyState = (owner) => ({
  owner,
  color: DEFAULT_BRAND_COLOR,
  ready: false,
  saving: false,
  error: null,
});

export function AppearanceProvider({ children }) {
  const { user, token } = useAuth();
  const owner = token && user?.id ? user.id : null;
  const ownerRef = useRef(owner);
  ownerRef.current = owner;
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const generation = useRef(0);
  const pendingSave = useRef(null);
  const [state, setState] = useState(() => emptyState(owner));
  const [retry, setRetry] = useState(0);
  const current = state.owner === owner ? state : emptyState(owner);
  const brandColor =
    brandColorById(current.color) || brandColorById(DEFAULT_BRAND_COLOR);

  useEffect(() => {
    const request = ++generation.current;
    let cancelled = false;
    pendingSave.current = null;
    setState(emptyState(owner));
    if (!owner) return undefined;
    getAppearancePreferences(tokenRef.current)
      .then((data) => {
        if (
          cancelled ||
          ownerRef.current !== owner ||
          generation.current !== request
        )
          return;
        if (!brandColorById(data?.brandColor))
          throw new Error('Appearance preferences unavailable. Try again.');
        setState({
          owner,
          color: data.brandColor,
          ready: true,
          saving: false,
          error: null,
        });
      })
      .catch(() => {
        if (
          !cancelled &&
          ownerRef.current === owner &&
          generation.current === request
        ) {
          setState({
            ...emptyState(owner),
            error: 'Your colors could not be loaded. Try again.',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [owner, retry]);

  // Account switching resets the brand before paint. No browser-wide preference.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.dataset.brandColor = brandColor.id;
    root.style.setProperty('--ada-brand-color', brandColor.hex);
    const icon = document.querySelector(
      'link[rel="icon"][type="image/svg+xml"]'
    );
    if (icon)
      icon.href = `data:image/svg+xml,${encodeURIComponent(brandIconSVG(brandColor.hex))}`;
  }, [brandColor]);

  const saveBrandColor = useCallback(
    async (id) => {
      if (
        !owner ||
        !current.ready ||
        !brandColorById(id) ||
        pendingSave.current
      )
        return;
      if (id === current.color && !current.error) return;
      const request = generation.current;
      const marker = {};
      pendingSave.current = marker;
      setState((prev) => ({ ...prev, saving: true, error: null }));
      try {
        const data = await putAppearancePreferences(
          { brandColor: id },
          tokenRef.current
        );
        if (ownerRef.current !== owner || generation.current !== request)
          return;
        if (data?.brandColor !== id) throw new Error('Invalid save response');
        setState({ owner, color: id, ready: true, saving: false, error: null });
      } catch {
        if (ownerRef.current === owner && generation.current === request) {
          setState((prev) => ({
            ...prev,
            saving: false,
            error: 'That color could not be saved. Please try again.',
          }));
        }
      } finally {
        if (pendingSave.current === marker) pendingSave.current = null;
      }
    },
    [owner, current.ready, current.color, current.error]
  );

  return (
    <AppearanceContext.Provider
      value={{
        brandColor,
        ready: current.ready,
        saving: current.saving,
        error: current.error,
        saveBrandColor,
        reloadAppearance: () => setRetry((value) => value + 1),
      }}
    >
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  const value = useContext(AppearanceContext);
  if (!value)
    throw new Error('useAppearance must be used within AppearanceProvider');
  return value;
}
